'use client';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useScrollLock } from '@/hooks/useScrollLock';
import { track } from '@/lib/analytics/track';
import { BACKDROP_VARIANTS, getMotionVariants, MODAL_VARIANTS } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';
import { sfIcons } from '@/lib/sf-icons';
import { SFIcon } from '@/components/ui/sf-icon';

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

export function Modal({
  open,
  onOpenChange,
  children,
  className,
  size = 'md',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: ModalProps) {
  const prefersReducedMotion = useReducedMotion();
  const triggerRef = React.useRef<HTMLElement | null>(null);

  // Enable scroll lock when modal is open
  useScrollLock(open);

  // Track focus for return
  React.useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement as HTMLElement;
      track('modal_open', {
        size,
        reducedMotion: prefersReducedMotion
      });
    } else {
      // Return focus to trigger
      if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
        triggerRef.current.focus();
      }
    }
  }, [open, size, prefersReducedMotion]);

  // Keyboard handler
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && closeOnEscape) {
        e.preventDefault();
        track('modal_close', { reason: 'esc' });
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, closeOnEscape, onOpenChange]);

  // Focus trap
  React.useEffect(() => {
    if (!open) return;

    const modal = document.querySelector('[data-testid="modal"]');
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);

    // Focus first element
    setTimeout(() => firstElement?.focus(), 0);

    return () => document.removeEventListener('keydown', handleTabKey);
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      track('modal_close', { reason: 'backdrop' });
      onOpenChange(false);
    }
  };

  const handleCloseClick = () => {
    track('modal_close', { reason: 'close_button' });
    onOpenChange(false);
  };

  // Get motion variants that respect prefers-reduced-motion
  const backdropVariants = getMotionVariants(BACKDROP_VARIANTS, prefersReducedMotion);
  const modalVariants = getMotionVariants(MODAL_VARIANTS, prefersReducedMotion);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — Liquid Glass Backdrop (L1 Baseline per CANON v1.4.3) */}
          <motion.div
            data-testid="modal-backdrop"
            className={cn(
              "fixed inset-0 z-50",
              semanticTokens.glass.dark.surface.bg30,
              semanticTokens.glass.dark.surface.bgOverlay,
              "backdrop-blur-xl backdrop-saturate-[180%]",
              "dark:backdrop-saturate-150"
            )}
            variants={backdropVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            onClick={handleBackdropClick}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            data-testid="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
            variants={modalVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div
              className={cn(
                "relative w-full p-6 outline-none overflow-hidden",
                // Liquid Glass L1 Baseline (CANON v1.4.3): backdrop-blur-xl + полупрозрачный bg
                "rounded-[32px]",
                "bg-white/60",
                semanticTokens.glass.dark.surface.bg50,
                "backdrop-blur-xl",
                "border border-white/20 dark:border-white/10",
                "shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_25px_60px_-12px_hsl(220_15%_5%/0.30)]",
                "dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_25px_60px_-12px_hsl(0_0%_0%/0.6)]",
                sizeClasses[size],
                className
              )}
            >
              {/* Specular highlight (top edge reflection) — Liquid Glass signature (L2 Enhanced) */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/20 rounded-t-[32px]" />

                {/* Close button - positioned at top-right of modal container, not content */}
              {showCloseButton && (
                <button
                  onClick={handleCloseClick}
                  className={cn(
                    "absolute right-4 top-4 z-20",
                    "flex items-center justify-center",
                    "w-8 h-8 rounded-full",
                    "bg-black/5 dark:bg-white/10",
                    "hover:bg-black/10 dark:hover:bg-white/20",
                    "opacity-70 hover:opacity-100",
                    "transition-all duration-240",
                    "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  )}
                  data-testid="modal-close"
                  aria-label="Close modal"
                >
                  <SFIcon icon={sfIcons.close} className="h-4 w-4" />
                </button>
              )}

              {/* Content container */}
              <div className="relative z-10 pr-8">
                {children}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* Modal subcomponents */
export const ModalHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("mb-4 space-y-1.5", className)}
    {...props}
  />
));
ModalHeader.displayName = "ModalHeader";

export const ModalTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
ModalTitle.displayName = "ModalTitle";

export const ModalDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
ModalDescription.displayName = "ModalDescription";

export const ModalFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
    {...props}
  />
));
ModalFooter.displayName = "ModalFooter";